import type { ExportEnvelope } from '../../domain/backup/ExportEnvelope';
import type { BackupMetadata } from '../../domain/backup/BackupMetadata';
import type { BackupMetadataId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { BackupRepository } from '../ports/BackupRepository';
import type { BackupTransactions } from '../ports/BackupTransactions';
import type { Clock } from '../ports/Clock';
import type { IdGenerator } from '../ports/IdGenerator';

export interface ImportProfileDataResult {
  /** The profile's own state immediately before the write — offered to the caller for download. */
  readonly safetyBackup: ExportEnvelope;
}

/**
 * product/L0_DECISIONS_CLOSED.md §7 — full replacement only, never a merge. A safety backup of
 * the current state is produced before any write (AC-L0-IMP-01); the replacement itself runs in
 * one transaction, so a failure leaves the prior state untouched (AC-DATA-08).
 */
export class ImportProfileData {
  public constructor(
    private readonly transactions: BackupTransactions,
    private readonly backups: BackupRepository,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(
    profileId: ProfileId,
    envelope: ExportEnvelope,
  ): Promise<Result<ImportProfileDataResult, RoutineError>> {
    try {
      const timestamp = this.clock.now().toISOString();
      const safetyBackup = await this.transactions.exportProfileData(profileId, timestamp);
      const metadata: BackupMetadata = {
        id: this.ids.generate<BackupMetadataId>(),
        profileId,
        backupVersion: safetyBackup.version,
        status: 'pre_import_snapshot',
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await this.backups.save(metadata);
      await this.transactions.replaceProfileData(profileId, envelope);
      return success({ safetyBackup });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
